import React, { useState, useRef } from 'react';
import { FaInstagram, FaSnapchatGhost, FaWhatsapp } from 'react-icons/fa';
import GeneralButton from '../../../mainComponents/generalButton';
import sticker from '../../../../assets/sticker.png';
import first from '../../../../assets/1.jpeg';
import second from '../../../../assets/2.jpeg';
import third from '../../../../assets/3.jpeg';
import fourth from '../../../../assets/4.jpeg';
import { enhancedShare, canShare } from '../../../utils/webviewDetector';
 
// Interface for component props
interface ShareDrawerContentProps {
    handleDelete?: () => void;
    handleBlock?: () => void;
    handleReport?: () => void;
    profileImage?: string;
    username?: string;
    shareLink?: string;
}

const ShareDrawerContent: React.FC<ShareDrawerContentProps> = ({
    profileImage,
    username,
    shareLink
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const totalSteps = {
        instagram: 4,
        snapchat: 1,
        whatsapp: 1
    };

    const stepContent = {
        instagram: [
            {
                title: "Click the sticker button",
                image: first
            },
            {
                title: "Search for the share link sticker",
                image: second
            },
            {
                title: "Tap on the link sticker",
                image: third
            },
            {
                title: "Share your story",
                image: fourth
            }
        ],
        snapchat: [
            {
                title: "Add link to your story",
                image: first
            }
        ],
        whatsapp: [
            {
                title: "Share your link",
                image: first
            }
        ]
    };

    const [selectedPlatform, setSelectedPlatform] = useState('instagram');

    const handleNextStep = () => {
        const maxSteps = totalSteps[selectedPlatform as keyof typeof totalSteps];
        if (currentStep < maxSteps) {
            setCurrentStep(currentStep + 1);
        }
        // If we're on the last step, the share button will be shown automatically
    };

    const handleStepClick = (step: number) => {
        setCurrentStep(step);
    };

    const handlePlatformChange = (platform: string) => {
        setSelectedPlatform(platform);
        setCurrentStep(1);
    };

    // Function to create and share image
    const createShareImage = async () => {
        if (!canvasRef.current || !profileImage || !shareLink) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        canvas.width = 1080;
        canvas.height = 1200;

        // Load and draw profile image as background
        const img = new Image();
        img.crossOrigin = 'anonymous';

        return new Promise<void>((resolve) => {
            img.onload = () => {
                // Draw profile image as full background (scaled to cover)
                const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
                const scaledWidth = img.width * scale;
                const scaledHeight = img.height * scale;
                const x = (canvas.width - scaledWidth) / 2;
                const y = (canvas.height - scaledHeight) / 2;

                // Draw the background image
                ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

                // Add blur effect by drawing multiple semi-transparent layers
                for (let i = 0; i < 8; i++) {
                    ctx.fillStyle = `rgba(0, 0, 0, ${0.3 - i * 0.03})`;
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }

                // Draw profile image in center
                const imgSize = 300;
                const imgX = (canvas.width - imgSize) / 2;
                const imgY = (canvas.height - imgSize) / 2 - 200;

                // Create circular mask for profile image
                ctx.save();
                ctx.beginPath();
                ctx.arc(imgX + imgSize / 2, imgY + imgSize / 2, imgSize / 2, 0, 2 * Math.PI);
                ctx.clip();
                ctx.drawImage(img, imgX, imgY, imgSize, imgSize);
                ctx.restore();

                // Add username
                ctx.fillStyle = 'white';
                ctx.font = 'bold 48px Arial';
                ctx.textAlign = 'center';
                ctx.fillText(`@${username || 'username'}`, canvas.width / 2, imgY + imgSize + 80);

                // Add call to action
                ctx.fillStyle = 'white';
                ctx.font = 'bold 36px Arial';
                ctx.fillText('Send me anonymous messages!', canvas.width / 2, imgY + imgSize + 140);

                resolve();
            };
            img.src = profileImage;
        });
    };

    const handleShareImage = async () => {
        try {
            await createShareImage();

            if (canvasRef.current) {
                canvasRef.current.toBlob(async (blob) => {
                    if (blob) {
                        const file = new File([blob], 'share-image.png', { type: 'image/png' });

                        try {
                            await enhancedShare({
                                title: 'Check out my anonymous Q&A!',
                                text: `Send me anonymous messages at ${shareLink}`,
                                files: [file]
                            });
                        } catch (error) {
                            console.error('Enhanced share failed, falling back to download:', error);
                            // Fallback: download the image
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'share-image.png';
                            a.click();
                            URL.revokeObjectURL(url);
                        }
                    }
                }, 'image/png');
            }
        } catch (error) {
            console.error('Error sharing image:', error);
        }
    };

    const currentStepContent = stepContent[selectedPlatform as keyof typeof stepContent];
    const maxSteps = totalSteps[selectedPlatform as keyof typeof totalSteps];

    return (
        <div className="relative">
            {/* Platform toggles */}
            <div className="flex justify-center gap-4 mt-4">
                <div
                    className={`px-7 py-2 rounded-full ${selectedPlatform === 'instagram' ? 'bg-white' : 'bg-gray-500'}`}
                    onClick={() => handlePlatformChange('instagram')}
                >
                    <FaInstagram
                        className={`text-lg size-6 ${selectedPlatform === 'instagram' ? 'text-black' : 'text-white'}`}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
                <div
                    className={`px-7 py-2 rounded-full ${selectedPlatform === 'snapchat' ? 'bg-white' : 'bg-gray-500'}`}
                    onClick={() => handlePlatformChange('snapchat')}
                >
                    <FaSnapchatGhost
                        className={`text-lg size-6 ${selectedPlatform === 'snapchat' ? 'text-black' : 'text-white'}`}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
                <div
                    className={`px-7 py-2 rounded-full ${selectedPlatform === 'whatsapp' ? 'bg-white' : 'bg-gray-500'}`}
                    onClick={() => handlePlatformChange('whatsapp')}
                >
                    <FaWhatsapp
                        className={`text-lg size-6 ${selectedPlatform === 'whatsapp' ? 'text-black' : 'text-white'}`}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
            </div>

            {/* Tutorial content */}
            <div className="mt-5 px-8 bg-white rounded-3xl shadow-lg pt-1 text-black">
                <h1 className="text-2xl font-semibold text-center mb-2 mt-2">How to add the Link<br /> to your story</h1>

                {maxSteps > 1 && (
                    <div className="flex justify-center gap-4 mb-3">
                        {Array.from({ length: maxSteps }, (_, i) => i + 1).map((num) => (
                            <div
                                key={num}
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-md font-bold cursor-pointer transition-colors duration-300
                                ${num === currentStep ? 'bg-black text-white' : 'bg-gray-300 text-black'}`}
                                onClick={() => handleStepClick(num)}
                            >
                                {num}
                            </div>
                        ))}
                    </div>
                )}

                <div className="text-xl font-semibold text-center mb-4">
                    {currentStepContent[currentStep - 1].title}
                    {currentStep === 1 && selectedPlatform === 'instagram' && (
                        <span className="inline-block ml-2">
                            <img src={sticker} />
                        </span>
                    )}
                </div>

                <div className="rounded-2xl overflow-hidden mb-4">
                    <img src={currentStepContent[currentStep - 1].image} alt={`Step ${currentStep}`} />
                </div>

                <div className="flex justify-between mb-4">
                    {currentStep < maxSteps ? (
                        <GeneralButton
                            text="Next Step"
                            color="black"
                            textColor="white"
                            onClick={handleNextStep}
                        />
                    ) : (
                        <GeneralButton
                            text="Share Image"
                            color="black"
                            textColor="white"
                            onClick={handleShareImage}
                        />
                    )}
                </div>
            </div>

            {/* Hidden canvas for image generation */}
            <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
                width={1080}
                height={1920}
            />

            {/* Spacer */}
            <div className="h-10"></div>
        </div>
    );
};

export default ShareDrawerContent;